import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

const emptyForm = {
  tax_profile_id: '',
  tariff_from: '',
  tariff_to: '',
  gst_rate: '',
  itc_allowed: false,
  is_active: true,
  sort_order: ''
};

function RoomTaxPolicyMaster({ token, selectedPropertyId }) {
  const [rows, setRows] = useState([]);
  const [taxProfiles, setTaxProfiles] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const resolvePropertyId = () => String(selectedPropertyId || localStorage.getItem('selectedPropertyId') || '').trim();
  const authHeaders = (extra = {}) => ({ Authorization: `Bearer ${token}`, 'x-property-id': resolvePropertyId(), ...extra });

  const loadRows = async () => {
    if (!resolvePropertyId()) {
      setRows([]);
      setError('Select a property first from Hotel Config');
      return;
    }
    try {
      const res = await fetch('/api/room-tax-policy', { headers: authHeaders() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) {
        setError(data?.error || 'Failed to load room tax policy');
        return;
      }
      setRows(Array.isArray(data) ? data : []);
      setError('');
    } catch (_) {
      setError('Failed to load room tax policy');
    }
  };

  const loadTaxProfiles = async () => {
    if (!resolvePropertyId()) {
      setTaxProfiles([]);
      return;
    }
    try {
      const res = await fetch('/api/tax-master', { headers: authHeaders() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) {
        setTaxProfiles([]);
        return;
      }
      setTaxProfiles(Array.isArray(data) ? data : []);
    } catch (_) {
      setTaxProfiles([]);
    }
  };

  useEffect(() => {
    setEditingId(null);
    setForm(emptyForm);
    loadRows();
    loadTaxProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedPropertyId]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
  };

  const save = async () => {
    if (!resolvePropertyId()) return setError('Select a property first from Hotel Config');
    const payload = {
      tax_profile_id: Number(form.tax_profile_id || 0),
      tariff_from: Number(form.tariff_from || 0),
      tariff_to: String(form.tariff_to || '').trim() === '' ? null : Number(form.tariff_to),
      gst_rate: Number(form.gst_rate || 0),
      itc_allowed: form.itc_allowed === true,
      is_active: form.is_active !== false,
      sort_order: Number(form.sort_order || 1)
    };
    try {
      const url = editingId ? `/api/room-tax-policy/${editingId}` : '/api/room-tax-policy';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data?.error || 'Save failed');
      resetForm();
      loadRows();
    } catch (_) {
      setError('Save failed');
    }
  };

  const editRow = (row) => {
    setEditingId(row.id);
    setForm({
      tax_profile_id: row.tax_profile_id ? String(row.tax_profile_id) : '',
      tariff_from: row.tariff_from ?? '',
      tariff_to: row.tariff_to ?? '',
      gst_rate: row.gst_rate ?? '',
      itc_allowed: row.itc_allowed === true,
      is_active: row.is_active !== false,
      sort_order: row.sort_order ?? ''
    });
    setError('');
    scrollToForm();
  };

  const removeRow = async (id) => {
    if (!resolvePropertyId()) return setError('Select a property first from Hotel Config');
    try {
      const res = await fetch(`/api/room-tax-policy/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data?.error || 'Delete failed');
      loadRows();
    } catch (_) {
      setError('Delete failed');
    }
  };

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', marginBottom: '0.6rem' }}>
        <span style={{ color: '#6b7280', fontWeight: 500 }}>Hotel Config</span>
        <span style={{ color: '#cbd5e0' }}>›</span>
        <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Room Tax Policy</span>
      </div>
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>Hotel Room Tax Policy</h2>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>Configure tariff-based GST slabs linked to tax profiles</p>
      {error ? <div className="error-banner">{error}</div> : null}

      <div ref={formRef} className="filter-bar" style={{ marginBottom: '1.2rem', ...editHighlightStyle(editHighlight) }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--theme-text)' }}>{editingId ? 'Edit Tax Slab' : 'Add Tax Slab'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.7rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Tax Profile</label>
            <select
              value={form.tax_profile_id}
              onChange={(e) => {
                const id = e.target.value;
                const profile = taxProfiles.find((t) => String(t.id) === String(id));
                const direct = Number(profile?.tax_percent || 0);
                const computed = Number(profile?.cgst_percent || 0) + Number(profile?.sgst_percent || 0) + Number(profile?.igst_percent || 0);
                const effective = direct > 0 ? direct : computed;
                setForm({ ...form, tax_profile_id: id, gst_rate: profile ? String(effective) : '' });
              }}
              style={{ width: '100%', padding: '8px' }}
            >
              <option value="">Select Tax Profile</option>
              {taxProfiles.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.tax_code} - {t.tax_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Tariff From</label>
            <input type="number" min="0" step="0.01" placeholder="0.00" value={form.tariff_from} onChange={(e) => setForm({ ...form, tariff_from: e.target.value })} style={{ width: '100%', padding: '8px' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Tariff To</label>
            <input type="number" min="0" step="0.01" placeholder="Blank = no limit" value={form.tariff_to} onChange={(e) => setForm({ ...form, tariff_to: e.target.value })} style={{ width: '100%', padding: '8px' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>GST Rate %</label>
            <input type="number" min="0" step="0.001" placeholder="Auto-filled" value={form.gst_rate} readOnly style={{ width: '100%', padding: '8px', opacity: 0.6 }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: 'var(--theme-text)' }}>Sort Order</label>
            <input type="number" min="1" step="1" placeholder="1" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} style={{ width: '100%', padding: '8px' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem' }}>
              <input type="checkbox" checked={form.itc_allowed} onChange={(e) => setForm({ ...form, itc_allowed: e.target.checked })} />
              ITC Allowed
            </label>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem' }}>
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active
            </label>
          </div>
        </div>
        <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={save} className="btn-primary">{editingId ? 'Update Slab' : 'Add Slab'}</button>
          <button onClick={resetForm} className="btn-muted">Clear</button>
        </div>
      </div>

      {!rows.length ? (
        <div style={{ color: '#9ca3af', textAlign: 'center', padding: '2rem', background: '#fafafa', borderRadius: '8px', border: '1px dashed var(--theme-border)' }}>
          No room tax policy slabs found for this property. Add your first slab above.
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Order</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Tax Profile</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Tariff From</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Tariff To</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>GST %</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>ITC</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                  <td style={{ padding: '0.6rem' }}>{row.sort_order}</td>
                  <td style={{ padding: '0.6rem' }}>{row.tax_code ? `${row.tax_code} - ${row.tax_name || ''}` : '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{Number(row.tariff_from || 0).toFixed(2)}</td>
                  <td style={{ padding: '0.6rem' }}>{row.tariff_to == null ? 'No Limit' : Number(row.tariff_to).toFixed(2)}</td>
                  <td style={{ padding: '0.6rem' }}>{Number(row.gst_rate || 0).toFixed(3)}%</td>
                  <td style={{ padding: '0.6rem' }}><span className={row.itc_allowed ? 'tag tag-info' : 'tag tag-muted'}>{row.itc_allowed ? 'Yes' : 'No'}</span></td>
                  <td style={{ padding: '0.6rem' }}><span className={row.is_active ? 'tag tag-success' : 'tag tag-danger'}>{row.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td style={{ padding: '0.6rem', display: 'flex', gap: '0.45rem' }}>
                    <button onClick={() => editRow(row)} className="btn-primary btn-sm">Edit</button>
                    <button onClick={() => removeRow(row.id)} className="btn-danger btn-sm">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default RoomTaxPolicyMaster;
